<script setup lang="ts">
import { ref } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { formatDate, formatExamDistance, formatDuration } from '@/helpers/formatters'
import { currentUser } from '@/lib/currentUser'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()

import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import LoginCertificate from '@/ui-kit/login/Certificate.vue'
import AceProlongModal from '@/ui-kit/components/AceProlongModal/AceProlongModal.vue'

interface EntryPoint {
  title: string
  description: string
  duration: number
  cardLink: string
}

interface SeminarButton {
  text: string
  link: string
  unavailable_reason?: string
}

interface Seminar {
  title: string
  description: string
  language: string
  status: string
  has_exam: boolean
  start_date: string
  finish_date: string
  left_buttons: SeminarButton[]
}

interface MethodistLink {
  text: string
  url: string
}

interface DashboardContext {
  profile_image_url: string
  is_tutor: boolean
  incoming_tutor_requests_count: number
  incoming_online_teaching_requests_count: number
  methodist_links: MethodistLink[]
  has_active_aces: boolean
  is_partner: boolean
  partner_eula_accepted: boolean
  my_education?: Seminar[]
  entry_points?: EntryPoint[]
  rules_url: string
}

const ctx = usePageContext<DashboardContext>()
const { isMobile } = useScreen()

const user = currentUser
const profileUrl = '/teacher/bio/my'
const certificateModal = ref(false)
const aceProlongModal = ref<{ $open: () => void } | null>(null)
const emptyQuery = new URLSearchParams()
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="teacher" :rules-url="ctx.rules_url" />

    <div class="content teacher-dashboard">
      <v-notifications position="bottom right" />
      <div class="header mb-4">Рабочий стол</div>

      <div class="teacher-dashboard__grid">
        <div class="flex-adaptive teacher-dashboard__grid-buttons mb-2">
          <v-button mode="primary filled x-large block" :href="profileUrl">
            <div class="d-flex align-center py-1 text-title-bold">
              <img :src="ctx.profile_image_url" class="teacher-dashboard__btn-avatar mr-3" />
              Моя визитка
            </div>
          </v-button>

          <div class="mx-2 my-1" />

          <v-button mode="warn filled x-large block" href="/journal/schedule">
            <div class="d-flex align-center py-1 text-title-bold">
              <v-icon class="mr-3" size="28">mdi-book-open-blank-variant</v-icon>
              Журнал
            </div>
          </v-button>
        </div>

        <div class="d-flex flex-column teacher-dashboard__grid-aside">
          <v-link mode="primary" href="/teacher/route" class="mb-2 text-title-bold"> Маршрут роста </v-link>
          <v-link mode="primary" href="/seminars/my_education" class="mb-2 text-title-bold"> Моё обучение </v-link>
          <v-link v-if="isMobile" mode="primary" href="/website/news/1" class="mb-4 text-title-bold">
            Наши новости
            <img :src="asset('img/method-logo.webp')" style="width: 24px; height: 24px; margin-left: 8px" />
          </v-link>

          <div v-if="!isMobile" class="my-2" />

          <div class="teacher-dashboard__card teacher-dashboard__card_blue">
            <v-icon class="teacher-dashboard__card-icon" size="32">student-cap</v-icon>
            <h2 class="teacher-dashboard__card-title">Онлайн-преподавание</h2>
            <div class="teacher-dashboard__card-links">
              <div class="teacher-dashboard__link-badge" :data-badge-num="ctx.incoming_online_teaching_requests_count">
                <v-link mode="primary" class="mr-2 mb-half" href="/online_teaching/incoming_trial_lesson_requests">
                  Мои заявки
                </v-link>
              </div>
              <v-link mode="primary" class="mr-2 mb-half" href="/online_teaching/trial_lesson_offer">
                Моя анкета
              </v-link>
              <v-link mode="primary" class="mr-2" href="/teacher/bulk_smartbook_manage"> Оптовая закупка УУ </v-link>
            </div>
          </div>

          <div v-if="ctx.is_partner" class="teacher-dashboard__card teacher-dashboard__card_green">
            <v-icon class="teacher-dashboard__card-icon" size="32">partner</v-icon>
            <h2 class="teacher-dashboard__card-title">Партнёрская программа</h2>
            <div v-if="ctx.partner_eula_accepted" class="teacher-dashboard__card-links">
              <v-link mode="primary" class="mr-2 mb-half" href="/partners/products">Ссылки и реклама</v-link>
              <v-link mode="primary" class="mr-2" href="/partners/stats">Статистика</v-link>
            </div>
            <div v-else class="teacher-dashboard__card-links">
              <v-link mode="primary" class="mr-2 mb-half" href="/partners/eula">Ознакомиться с правилами</v-link>
            </div>
          </div>

          <div v-if="ctx.is_tutor" class="teacher-dashboard__card teacher-dashboard__card_orange">
            <v-icon class="teacher-dashboard__card-icon" size="32">cert-tutor-done</v-icon>
            <h2 class="teacher-dashboard__card-title">Кабинет тьютора</h2>
            <div class="teacher-dashboard__card-links">
              <div class="teacher-dashboard__link-badge" :data-badge-num="ctx.incoming_tutor_requests_count">
                <v-link mode="primary" class="mr-2" href="/tutors/tutor_cabinet">Мои заявки</v-link>
              </div>
              <v-link mode="primary" class="mr-2" href="/tutors/edit_offer">Моя анкета</v-link>
            </div>
          </div>

          <div v-if="ctx.methodist_links.length" class="teacher-dashboard__card teacher-dashboard__card_red">
            <v-icon class="teacher-dashboard__card-icon" size="32">crown</v-icon>
            <h2 class="teacher-dashboard__card-title">Кабинет методиста</h2>
            <div class="teacher-dashboard__card-links" style="flex-direction: column">
              <v-link
                v-for="(link, i) in ctx.methodist_links"
                :key="i"
                mode="primary"
                class="mr-2 mb-half"
                :href="link.url"
              >
                &bullet; {{ link.text }}
              </v-link>
            </div>
          </div>

          <div v-if="ctx.has_active_aces" class="teacher-dashboard__card teacher-dashboard__card_red">
            <v-icon class="teacher-dashboard__card-icon" size="32">certificate</v-icon>
            <h2 class="teacher-dashboard__card-title">Сертификаты АС</h2>
            <div class="teacher-dashboard__card-links">
              <v-link mode="primary" @click="aceProlongModal?.$open()">Продление сертификатов</v-link>
            </div>
          </div>
        </div>

        <div v-if="!user.hasTeacherPurchase" class="d-flex flex-column teacher-dashboard__grid-content">
          <v-rich-modal v-model="certificateModal" title="Сертификат">
            <LoginCertificate standalone :query="emptyQuery" @close="certificateModal = false" />
          </v-rich-modal>

          <div v-if="!user.id" class="teacher-dashboard__demo-card mb-3">
            Добро пожаловать в Демо-кабинет учителя на сайте МирГоворит!
          </div>

          <p class="text-body mb-3">
            Для того чтобы начать работу в личном кабинете, необходимо пройти один из следующих семинаров:
          </p>

          <div class="teacher-dashboard__entrypoints-grid">
            <a
              v-for="item in ctx.entry_points"
              :key="item.title"
              class="teacher-dashboard__entrypoint-card"
              :href="item.cardLink"
            >
              <span class="teacher-dashboard__entrypoint-title mb-half" v-text="item.title" />
              <span class="d-flex align-center teacher-dashboard__entrypoint-duration mb-1">
                <v-icon class="mr-1 gray-text" size="16">mdi-clock</v-icon>
                {{ formatDuration(item.duration) }}
              </span>
              <p class="text-caption mb-3 flex-spacer" v-text="item.description" />
              <v-button mode="primary large" :href="item.cardLink">К семинару</v-button>
            </a>
          </div>

          <div class="mt-2">
            <v-button v-if="user.id" class="mt-1" mode="primary flat" @click="certificateModal = true">
              У меня уже есть сертификат
            </v-button>
          </div>
        </div>

        <div v-else class="d-flex flex-column teacher-dashboard__grid-content">
          <h2 class="text-title-bold mb-2">Текущее</h2>

          <div v-for="seminar in ctx.my_education" :key="seminar.title" class="teacher-dashboard__seminar">
            <div class="flex-adaptive justify-space-between mb-1">
              <div class="teacher-dashboard__seminar-badges">
                <template v-if="seminar.status === 'passed'">
                  <div v-if="seminar.has_exam" class="teacher-dashboard__seminar-badge blue">Экзамен сдан</div>
                  <div class="teacher-dashboard__seminar-badge dark-blue">Пройден</div>
                </template>

                <div v-if="seminar.status === 'passed_offline'" class="teacher-dashboard__seminar-badge dark-blue">
                  Пройден офлайн
                </div>

                <template v-if="seminar.status === 'exam_in_progress'">
                  <div class="teacher-dashboard__seminar-badge red">Экзамен сегодня</div>
                  <div class="teacher-dashboard__seminar-badge green">Идёт</div>
                </template>

                <template v-if="seminar.status === 'active'">
                  <div v-if="seminar.has_exam" class="teacher-dashboard__seminar-badge orange">
                    {{ formatExamDistance(seminar.finish_date) }}
                  </div>
                  <div class="teacher-dashboard__seminar-badge green">Идёт</div>
                </template>

                <template v-if="seminar.status === 'future'">
                  <div class="teacher-dashboard__seminar-badge gray">Начнётся {{ formatDate(seminar.start_date) }}</div>
                </template>
              </div>

              <div class="text-overline gray-text" v-text="seminar.language" />
            </div>

            <div class="text-body-bold my-1" v-text="seminar.title" />
            <p class="mb-2" v-text="seminar.description" />

            <div class="teacher-dashboard__seminar-actions text-body-bold">
              <v-link v-for="(btn, i) in seminar.left_buttons" :key="`l${i}`" mode="primary" :href="btn.link">
                {{ btn.text }}
              </v-link>
            </div>
          </div>
        </div>
      </div>

      <AceProlongModal ref="aceProlongModal" />
      <v-footer />
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/teacher/dashboard.scss' as *;
</style>
